import styled from "styled-components";
import {createCard, topbar} from "theme/tokens";
import {EditorialColumn, HERO_BOTTOM_INSET_PX, Hero, HeroLayout, Landing, LandingContent} from "home/HomePage.styles";
import {CAMPAIGN_SIDE_BY_SIDE_MIN_WIDTH_PX, CAMPAIGN_SPACE_GAP_PX} from "./campaignSpace";
import {ArtHitArea, CampaignCopy, CampaignReading, CampaignSlot} from "./LandingCampaign.styles";
import {BAT_CURSOR_IMAGE} from "./halloweenEdition";
import {RECIPIENT_BOTTOM_GAP_PX, RECIPIENT_CAMPAIGN_SLOT_HEIGHT_PX, RECIPIENT_HEADER_GAP_PX} from "./campaignFit";

/** Keep one card tree while campaign eligibility changes; the fallback has no campaign reservation. */
export const CampaignSurfaceRoot = styled(Landing)`
	/*
	 * THE LANDING COPY RESTS ABOVE THE COOKIE BAR while the banner is undecided (owner decision
	 * 2026-09-18, ui#2596), consuming the same reserve the recipient surface below takes as its
	 * bottom clearance. The campaign reading is centred in the hero's content box, so a bar that
	 * grows (the notice gained a sentence) otherwise reaches up into the copy: the Romanian newspaper
	 * slide ran 21px into it at 901x700, and no copy cut cleared it. The reading's bottom inset takes
	 * the part of the reserve the hero's own inset does not already cover, which moves the centred copy
	 * up by half of it, clear of the bar at every viewport measured (en/ro/de at 901x700, 1366x768 and
	 * 1920x1080).
	 *
	 * The inset is on the READING, not on the hero: raising the hero's inset re-centres the create
	 * card as well, and that card is on screen before the banner mounts, so the card jumped 35px on
	 * load at 901x700 (CLS 0.0117 against the 0.01 load budget, all three locales). The copy fades in
	 * after the campaign is assigned, so moving only it costs the load nothing.
	 *
	 * Side-by-side band only: below 901px the stacked band already lays out around the bar through
	 * campaignSpace, and the reading is then a FIXED strip placed by data-mobile-space, whose insets
	 * this rule must never overwrite — hence the :not(), even though useCampaignSpace removes that
	 * attribute at 901px and wider. The banner removes the variable once answered, so the inset
	 * returns to 0 and the copy re-centres in the whole box after the decision.
	 */
	&[data-campaign-surface="landing"] {
		@media (min-width: ${CAMPAIGN_SIDE_BY_SIDE_MIN_WIDTH_PX}px) {
			${CampaignReading}:not([data-mobile-space]) { inset: 0 0 max(0px, var(--consent-bar-reserve, 0px) - ${HERO_BOTTOM_INSET_PX}px); }
		}
		/*
		 * Stacked, with campaignSpace measured and no room on screen for the promotion: the reading folds to
		 * nothing instead of being drawn at full height from the card's bottom, past an allocation the
		 * reservation had already cut to the room left (0 when the column fills the surface). Drawn, it
		 * lengthened the landing's scroll by its own height: 185px at 320x568 with the Request files tab taking
		 * the room above the card and the cookie banner undecided, and 284px under a phone's composing card
		 * (kitecorp/transferify-ui#3410). It returns, placed, as soon as a measurement finds room, which a scroll
		 * or the banner's answer re-asks. The landing only: the recipient page reserves an in-flow slot of
		 * its own for this reading. Before the first measurement the attribute is absent, so the prerendered
		 * page is unchanged.
		 */
		@media (max-width: ${CAMPAIGN_SIDE_BY_SIDE_MIN_WIDTH_PX - 1}px) {
			${CampaignReading}[data-no-space] { height: 0; overflow: hidden; }
		}
	}
	/*
	 * The bat (kitecorp/transferify-ui#3776): the pointer over the landing while its Halloween slide is up, for a
	 * fine pointer that hovers, the only pointer a cursor image can reach. The surface sets it so the copy, the
	 * composer card's own ground and the gaps between take it by inheritance; the artwork's hit area keeps its
	 * pointer fallback, so a browser that refuses the image still shows that the art is pressable; the real
	 * controls keep their own cursors, as under every other slide. (No backticks in a comment inside a
	 * styled-components literal: one would end it.)
	 */
	&[data-halloween] {
		@media (hover: hover) and (pointer: fine) {
			--tf-bat-cursor: ${BAT_CURSOR_IMAGE};
			cursor: var(--tf-bat-cursor), auto;
			${ArtHitArea} { cursor: var(--tf-bat-cursor), pointer; }
		}
	}
	&[data-campaign-surface="recipient"] {
		--tf-recipient-card-w: ${createCard.widthPx}px;
		/* Beside the campaign the file card is the create card's width again (ui#1922, ui#2888). */
		@media (min-width: ${CAMPAIGN_SIDE_BY_SIDE_MIN_WIDTH_PX}px) { --tf-recipient-card-w: ${createCard.sideBySideWidthPx}px; }
		--recipient-header-clearance: ${parseFloat(topbar.height) + RECIPIENT_HEADER_GAP_PX}px;
		--recipient-bottom-clearance: max(${RECIPIENT_BOTTOM_GAP_PX}px, var(--consent-bar-reserve, 0px));
		--recipient-campaign-reserve: ${RECIPIENT_CAMPAIGN_SLOT_HEIGHT_PX}px;
		/*
		 * The page's own scroller (ui#2457) is Landing itself: it keeps Landing's vertical auto and
		 * horizontal hidden rather than clipping both as it did while the CARD scrolled, so the card
		 * keeps its height when a window is too short for its fixed parts plus the reading list's
		 * floor, and this is the box that lets a visitor reach the download button underneath. Still
		 * never sideways.
		 *
		 * The min-height 0 and flex 1 pair this block used to restate moved onto Landing in ui#2394,
		 * where every surface needs it to leave room for the shell's legal footer row. Two copies of one
		 * rule is how the recipient page and the landing end up disagreeing about their own height.
		 * (No backticks in this comment: it lives INSIDE a template literal and one would end it.)
		 */
		${LandingContent} { height: 100%; min-height: 0; }
		/*
		 * BOTH CLEARANCES ARE THE HERO'S, NOT THE SURFACE'S (owner 2026-09-15, ui#2457: "the slides need
		 * to stretch from top to bottom of the page").
		 *
		 * They used to be padding on the surface itself, and that is exactly what kept the art short. The
		 * campaign's ArtworkLayer is an inset-0 layer inside LandingContent, which is an ordinary in-flow
		 * child, so the surface's own padding moved it: measured at 1280x900, the slides ran from y=90
		 * (the bar plus its gap) to y=884 instead of 0 to 900. The sender's own wallpaper hangs off the
		 * SURFACE rather than off LandingContent and was already full height, which is why only the
		 * slides were reported.
		 *
		 * Moving them one box inwards changes nothing about where the card sits: LandingContent now fills
		 * the surface, and the Hero's content box, which is what HeroLayout and the card column measure
		 * against, comes out exactly the same size it did when the surface held the padding.
		 * useRecipientCampaignFit reads the same total back for the same reason, from the Hero.
		 *
		 * No backticks in this comment: it lives INSIDE a template literal and one would end it.
		 */
		${Hero} {
			flex: 1;
			min-height: 0;
			padding-inline: 0;
			padding-block: var(--recipient-header-clearance) var(--recipient-bottom-clearance);
		}
		${HeroLayout} { min-height: 0; }
		[data-drop-container] {
			position: relative;
			pointer-events: auto;
			width: var(--tf-recipient-card-w);
			max-width: 100%;
			height: 100%;
			max-height: 100%;
			justify-content: flex-start;
			flex: none;
		}
		[data-recipient-card-frame] { position: relative; max-height: 100%; }
		[data-testid="recipient-card"] { margin-top: 0; }
		/*
		 * Keep hidden gifts measurable, so resizing can restore them without a fit/hide loop, and PINNED
		 * TO THE FRAME'S TOP so measuring them costs the page no scroll (ui#2457, later).
		 *
		 * Without the two offsets the box keeps its static position, which is under the card, and an
		 * absolutely positioned descendant still contributes to its containing block's scrollable
		 * overflow whether or not it is visible. Measured on the #2467 preview at 1440x800: a tag that
		 * did not fit reached 824px against a 800px window and the page scrolled by exactly that 24px,
		 * which is what the owner reported. Pinning it costs the measurement nothing, because what the
		 * fit decision reads is the box's own height.
		 */
		&:not([data-recipient-gift-fit]) [data-peel-position],
		&:not([data-recipient-gift-fit]) [data-peel-pulls] {
			position: absolute; top: 0; left: 0; visibility: hidden; pointer-events: none;
		}
		&[data-recipient-gift-fit] [data-peel-card] { max-height: calc(100% - var(--recipient-gift-reserve)); }
		/* SAFE centring (ui#2457): a card taller than its column — its fixed parts plus the reading
		   list's floor, on a short window — would otherwise be centred on the overflow too, sliding its
		   sender header up under the top bar where no scroll can reach it. Safe alignment falls back to
		   the start exactly then, so the whole card stays reachable below. */
		&[data-campaign-fit] [data-drop-container], &[data-own-art] [data-drop-container] { justify-content: safe center; }
		[data-testid="recipient-brand"] {
			position: absolute;
			top: 0;
			transform: translateY(-50%);
			margin: 0;
		}
		/* The card's position belongs to the SURFACE, not to which backdrop won (owner 2026-09-12:
		   "float atop the slides/wallpaper, not influenced by them" — and it stays on the right at
		   wide widths exactly where the campaign layout puts it). Only the plain, artless fallback
		   centres. */
		&:not([data-campaign-fit]):not([data-own-art]) {
			${HeroLayout} { justify-content: center; padding-inline: 20px; }
		}
		/* The fixed loader needs no column box. Creating that box only for the resolved card avoids
		   moving an empty full-height column from the fallback centre to the campaign's right side. */
		&[data-campaign-pending] [data-drop-container] { display: contents; }
		/* Loading has no content to fit yet, and stays at the viewport centre through cookie measurement. */
		&[data-campaign-pending] [data-testid="recipient-card"] {
			position: fixed;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			max-width: calc(100% - 32px);
		}

		@media (min-width: ${CAMPAIGN_SIDE_BY_SIDE_MIN_WIDTH_PX}px) {
			/* Equal insets centre the card; extra cookie clearance may only reduce the available room.
			   Stated as the whole inset rather than as the top-up it was until ui#2457, because the
			   bottom clearance is now on this same box: a top-up would have REPLACED it. */
			&[data-campaign-fit] ${Hero}, &[data-own-art] ${Hero} { padding-bottom: max(var(--recipient-bottom-clearance), var(--recipient-header-clearance)); }
			/* A hanging tag uses the space below the card, not a second decorative bottom inset. */
			&[data-campaign-fit]:has([data-peel-position]) ${Hero}, &[data-own-art]:has([data-peel-position]) ${Hero} { padding-bottom: var(--recipient-bottom-clearance); }
			/* Without the editorial column the space-between row would park the lone card LEFT;
			   auto margin holds it on the right, where the campaign places it. */
			&[data-own-art] [data-drop-container] { margin-left: auto; }
		}
		@media (max-width: ${CAMPAIGN_SIDE_BY_SIDE_MIN_WIDTH_PX - 1}px) {
			/* The list keeps the recipient primitives' own scrolling here too (ui#2457): it is the page's
			   one scroll region at every width, campaign or no campaign, and the stacked strip only
			   changes how much room the card's column has to divide. */
			${Hero} { padding-inline: 16px; }
			${HeroLayout} { height: 100%; }
			&:not([data-campaign-fit]) ${HeroLayout} { padding-inline: 0; }
			[data-drop-container] { order: 1; margin-inline: auto; }
			/* The open game takes the strip at its own height (kitecorp/transferify-ui#2889): the card column
			   gives up the room, and its reading list scrolls, rather than the game covering any of it. */
			&[data-arcade-open] { --recipient-campaign-reserve: calc(var(--recipient-arcade-height) + ${2 * CAMPAIGN_SPACE_GAP_PX + 2}px); }
			&[data-campaign-fit] [data-drop-container] { max-height: calc(100% - var(--recipient-campaign-reserve)); }
			${EditorialColumn} { flex: 0 0 var(--recipient-campaign-reserve); min-height: 0; }
			/* On a page with the sender's art the column exists only while its game is open, and it opens ABOVE
			   the card (kitecorp/transferify-ui#2990): the card gives up the same room below it. */
			&[data-own-art] ${EditorialColumn} { order: 0; }
			&[data-own-art][data-arcade-open] [data-drop-container] { max-height: calc(100% - var(--recipient-campaign-reserve)); }
			${CampaignSlot} { margin-block-start: 0; height: 100%; }
			/* Right of the consent-withdrawal control where the copy reaches its corner (cornerInset in campaignSpace,
			   kitecorp/transferify#536); the landing keeps its copy on the card's edge. A margin rather than a
			   narrower space, so the container the size queries read keeps its height. */
			${CampaignReading}[data-mobile-space] > ${CampaignCopy} { margin-inline-start: var(--campaign-corner-inset, 0px); }
		}
		@media (max-width: 640px) {
			--recipient-header-clearance: ${parseFloat(topbar.heightMobile) + RECIPIENT_HEADER_GAP_PX}px;
		}
	}
`;
